// ============================================================
// Chess Arena — Minimax Search with Alpha-Beta Pruning
// ============================================================
// Operates on lightweight BoardSnapshot objects that can be
// cloned cheaply for game-tree exploration without touching
// real game state.
// ============================================================

import {
  GridPos,
  PieceType,
  Team,
  TerrainType,
  BOARD_COLS,
  BOARD_ROWS,
} from '../core/types';
import { evaluatePosition } from './boardEvaluator';

// -----------------------------------------------------------------
// Lightweight board snapshot for search
// -----------------------------------------------------------------

export interface SnapPiece {
  id: string;
  type: PieceType;
  team: Team;
  pos: GridPos;
}

export class BoardSnapshot {
  pieces: SnapPiece[];
  private terrain: (TerrainType | null)[][]; // [row][col]

  constructor(pieces: SnapPiece[], terrain: (TerrainType | null)[][]) {
    this.pieces = pieces;
    this.terrain = terrain;
  }

  clone(): BoardSnapshot {
    const clonedPieces = this.pieces.map(p => ({
      ...p,
      pos: { ...p.pos },
    }));
    // Terrain doesn't change during search — share the reference
    return new BoardSnapshot(clonedPieces, this.terrain);
  }

  pieceAt(col: number, row: number): SnapPiece | null {
    return this.pieces.find(p => p.pos.col === col && p.pos.row === row) ?? null;
  }

  terrainAt(col: number, row: number): TerrainType | null {
    if (row < 0 || row >= BOARD_ROWS || col < 0 || col >= BOARD_COLS) return null;
    return this.terrain[row][col];
  }

  /** Apply a move: move piece to target, remove any captured piece. */
  applyMove(pieceId: string, target: GridPos): void {
    // Remove captured piece at target
    this.pieces = this.pieces.filter(
      p => !(p.pos.col === target.col && p.pos.row === target.row),
    );
    // Move the piece
    const piece = this.pieces.find(p => p.id === pieceId);
    if (piece) {
      piece.pos = { ...target };
    }
  }
}

// -----------------------------------------------------------------
// Move generation on snapshot (mirrors moveValidator logic)
// -----------------------------------------------------------------

const ORTHOGONAL: [number, number][] = [[0,-1],[0,1],[-1,0],[1,0]];
const DIAGONAL: [number, number][] = [[-1,-1],[1,-1],[-1,1],[1,1]];
const ALL_DIRS: [number, number][] = [...ORTHOGONAL, ...DIAGONAL];
const KNIGHT_OFFSETS: [number, number][] = [
  [-2,-1],[-2,1],[2,-1],[2,1],[-1,-2],[1,-2],[-1,2],[1,2],
];

function isValid(col: number, row: number): boolean {
  return col >= 0 && col < BOARD_COLS && row >= 0 && row < BOARD_ROWS;
}

function isPassable(col: number, row: number, snap: BoardSnapshot): boolean {
  return isValid(col, row) && snap.terrainAt(col, row) !== 'rock';
}

function getSnapMoves(piece: SnapPiece, snap: BoardSnapshot): GridPos[] {
  switch (piece.type) {
    case 'Pawn': return getPawnMoves(piece, snap);
    case 'Knight': return getKnightMoves(piece, snap);
    case 'Bishop': return getSlidingMoves(piece, DIAGONAL, snap);
    case 'Rook': return getSlidingMoves(piece, ORTHOGONAL, snap);
    case 'Queen': return getSlidingMoves(piece, ALL_DIRS, snap);
    case 'King': return getKingMoves(piece, snap);
  }
}

function getPawnMoves(piece: SnapPiece, snap: BoardSnapshot): GridPos[] {
  const moves: GridPos[] = [];
  const { col, row } = piece.pos;

  // Orthogonal movement (no capture)
  for (const [dc, dr] of ORTHOGONAL) {
    const c = col + dc, r = row + dr;
    if (!isPassable(c, r, snap)) continue;
    if (snap.pieceAt(c, r)) continue;
    moves.push({ col: c, row: r });
  }
  // Diagonal capture only
  for (const [dc, dr] of DIAGONAL) {
    const c = col + dc, r = row + dr;
    if (!isPassable(c, r, snap)) continue;
    if (snap.terrainAt(c, r) === 'bush') continue; // pawns can't diagonal into bush
    const target = snap.pieceAt(c, r);
    if (target && target.team !== piece.team) {
      moves.push({ col: c, row: r });
    }
  }
  return moves;
}

function getKnightMoves(piece: SnapPiece, snap: BoardSnapshot): GridPos[] {
  const moves: GridPos[] = [];
  for (const [dc, dr] of KNIGHT_OFFSETS) {
    const c = piece.pos.col + dc, r = piece.pos.row + dr;
    if (!isPassable(c, r, snap)) continue;
    const target = snap.pieceAt(c, r);
    if (target && target.team === piece.team) continue;
    moves.push({ col: c, row: r });
  }
  return moves;
}

function getKingMoves(piece: SnapPiece, snap: BoardSnapshot): GridPos[] {
  const moves: GridPos[] = [];
  for (const [dc, dr] of ALL_DIRS) {
    const c = piece.pos.col + dc, r = piece.pos.row + dr;
    if (!isPassable(c, r, snap)) continue;
    const target = snap.pieceAt(c, r);
    if (target && target.team === piece.team) continue;
    moves.push({ col: c, row: r });
  }
  return moves;
}

function getSlidingMoves(
  piece: SnapPiece,
  dirs: [number, number][],
  snap: BoardSnapshot,
): GridPos[] {
  const moves: GridPos[] = [];
  for (const [dc, dr] of dirs) {
    let c = piece.pos.col + dc;
    let r = piece.pos.row + dr;
    let steps = 0;
    while (isValid(c, r) && steps < 4) {
      if (snap.terrainAt(c, r) === 'rock') break;
      const target = snap.pieceAt(c, r);
      if (target) {
        if (target.team !== piece.team) moves.push({ col: c, row: r });
        break; // blocked either way
      }
      moves.push({ col: c, row: r });
      c += dc;
      r += dr;
      steps++;
    }
  }
  return moves;
}

// -----------------------------------------------------------------
// Generate all moves for a team
// -----------------------------------------------------------------

export interface ScoredMove {
  pieceId: string;
  target: GridPos;
  score: number;
}

function getAllMoves(snap: BoardSnapshot, team: Team): { pieceId: string; target: GridPos }[] {
  const moves: { pieceId: string; target: GridPos }[] = [];
  for (const piece of snap.pieces) {
    if (piece.team !== team) continue;
    const pieceMoves = getSnapMoves(piece, snap);
    for (const target of pieceMoves) {
      moves.push({ pieceId: piece.id, target });
    }
  }
  return moves;
}

// -----------------------------------------------------------------
// Move ordering — search captures and king threats first
// for better alpha-beta pruning
// -----------------------------------------------------------------

function orderMoves(
  moves: { pieceId: string; target: GridPos }[],
  snap: BoardSnapshot,
  team: Team,
): { pieceId: string; target: GridPos }[] {
  return moves.slice().sort((a, b) => {
    const captA = snap.pieceAt(a.target.col, a.target.row);
    const captB = snap.pieceAt(b.target.col, b.target.row);
    const scoreA = captA ? (captA.type === 'King' ? 10000 : pieceValue(captA.type)) : 0;
    const scoreB = captB ? (captB.type === 'King' ? 10000 : pieceValue(captB.type)) : 0;
    return scoreB - scoreA;
  });
}

function pieceValue(type: PieceType): number {
  const values: Record<PieceType, number> = {
    King: 10000, Pawn: 100, Knight: 320, Bishop: 330, Rook: 500, Queen: 900,
  };
  return values[type];
}

// -----------------------------------------------------------------
// Minimax with alpha-beta pruning
// -----------------------------------------------------------------

function minimax(
  snap: BoardSnapshot,
  depth: number,
  alpha: number,
  beta: number,
  maximizingTeam: Team,
  currentTeam: Team,
): number {
  // Terminal: king captured
  const hasMaxKing = snap.pieces.some(p => p.type === 'King' && p.team === maximizingTeam);
  const hasMinKing = snap.pieces.some(p => p.type === 'King' && p.team !== maximizingTeam);
  if (!hasMaxKing) return -99999 - depth; // losing sooner is worse
  if (!hasMinKing) return 99999 + depth;  // winning sooner is better

  if (depth === 0) {
    return evaluatePosition(snap, maximizingTeam);
  }

  const moves = orderMoves(getAllMoves(snap, currentTeam), snap, currentTeam);

  // No moves available — pass turn
  if (moves.length === 0) {
    return evaluatePosition(snap, maximizingTeam);
  }

  const isMaximizing = currentTeam === maximizingTeam;
  const nextTeam: Team = currentTeam === 'player' ? 'enemy' : 'player';

  if (isMaximizing) {
    let maxEval = -Infinity;
    for (const move of moves) {
      const child = snap.clone();
      child.applyMove(move.pieceId, move.target);
      const val = minimax(child, depth - 1, alpha, beta, maximizingTeam, nextTeam);
      maxEval = Math.max(maxEval, val);
      alpha = Math.max(alpha, val);
      if (beta <= alpha) break;
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const move of moves) {
      const child = snap.clone();
      child.applyMove(move.pieceId, move.target);
      const val = minimax(child, depth - 1, alpha, beta, maximizingTeam, nextTeam);
      minEval = Math.min(minEval, val);
      beta = Math.min(beta, val);
      if (beta <= alpha) break;
    }
    return minEval;
  }
}

// -----------------------------------------------------------------
// Public API
// -----------------------------------------------------------------

export interface AIDifficultyConfig {
  /** Search depth (1 = shallow/fast, 2 = medium, 3 = deep/strong). */
  depth: number;
  /** Random noise added to evaluation (0 = perfect, higher = weaker). */
  evalNoise: number;
  /** Probability of picking a random move instead of the best (0–1). */
  blunderRate: number;
  /** Seconds between AI move ticks. */
  moveInterval: number;
  /** Max pieces moved per tick. */
  movesPerTick: number;
}

export const DIFFICULTY_PRESETS: Record<string, AIDifficultyConfig> = {
  easy: {
    depth: 1,
    evalNoise: 200,
    blunderRate: 0.25,
    moveInterval: 2.0,
    movesPerTick: 1,
  },
  medium: {
    depth: 2,
    evalNoise: 80,
    blunderRate: 0.1,
    moveInterval: 1.2,
    movesPerTick: 2,
  },
  hard: {
    depth: 3,
    evalNoise: 20,
    blunderRate: 0.03,
    moveInterval: 0.8,
    movesPerTick: 2,
  },
  brutal: {
    depth: 3,
    evalNoise: 0,
    blunderRate: 0,
    moveInterval: 0.6,
    movesPerTick: 3,
  },
};

/**
 * Find the best move(s) for the given team using minimax search.
 * Returns moves sorted by score (best first).
 *
 * @param snap       Board snapshot to search from.
 * @param team       The team to find moves for.
 * @param difficulty AI difficulty configuration.
 * @param count      How many moves to return (for multi-move ticks).
 */
export function findBestMoves(
  snap: BoardSnapshot,
  team: Team,
  difficulty: AIDifficultyConfig,
  count: number = 1,
): ScoredMove[] {
  const allMoves = orderMoves(getAllMoves(snap, team), snap, team);
  if (allMoves.length === 0) return [];

  // Score each root move via minimax
  const nextTeam: Team = team === 'player' ? 'enemy' : 'player';
  const scored: ScoredMove[] = allMoves.map(move => {
    const child = snap.clone();
    child.applyMove(move.pieceId, move.target);
    const score = minimax(
      child,
      difficulty.depth - 1,
      -Infinity,
      Infinity,
      team,
      nextTeam,
    );
    // Add noise for imperfect play
    const noise = (Math.random() - 0.5) * 2 * difficulty.evalNoise;
    return { ...move, score: score + noise };
  });

  // Sort best first
  scored.sort((a, b) => b.score - a.score);

  // Blunder: chance to pick a random move instead
  const results: ScoredMove[] = [];
  const used = new Set<string>();

  for (let i = 0; i < count && scored.length > 0; i++) {
    let pick: ScoredMove;
    if (Math.random() < difficulty.blunderRate) {
      // Random blunder — pick any move
      pick = scored[Math.floor(Math.random() * scored.length)];
    } else {
      // Pick the best remaining move for a piece we haven't moved yet
      pick = scored.find(m => !used.has(m.pieceId)) ?? scored[0];
    }
    results.push(pick);
    used.add(pick.pieceId);
    // Remove this piece's moves from consideration
    const pieceId = pick.pieceId;
    const idx = scored.findIndex(m => m.pieceId === pieceId);
    if (idx >= 0) scored.splice(idx, 1);
  }

  return results;
}

/**
 * Create a BoardSnapshot from the current game state.
 * Called by AIController each tick before searching.
 */
export function createSnapshot(
  pieces: { id: string; type: PieceType; team: Team; pos: GridPos }[],
  terrainGrid: (TerrainType | null)[][],
): BoardSnapshot {
  const snapPieces: SnapPiece[] = pieces.map(p => ({
    id: p.id,
    type: p.type,
    team: p.team,
    pos: { ...p.pos },
  }));
  return new BoardSnapshot(snapPieces, terrainGrid);
}
